import React, { useState } from 'react';
import * as R from 'ramda';
import { Field } from 'redux-form';
import { Col, Row } from 'react-bootstrap';
import * as validations from './validations';
import * as constants from './constants';
import messages from './messages';
import {
  extractDateFormat,
  nonEmptyString,
  number,
  greaterOrEqual,
  ReadOnlyField,
  renderCurrencyField,
  renderDateField,
  renderTextField,
  required,
  validDate,
} from '../../../../../utils/redux-form-helper';
import FormattedDateOrDashes from '../../../../../components/FormattedDateOrDashes';
import { Link } from '@fortress-technology-solutions/fortress-component-library/Atoms';

function RentScheduleFieldArray({
  fields,
  startDate,
  intl,
  onRowDelete,
  onRowUpdate,
  onCreate,
  isFormValid,
  change,
}: Object) {
  const [editableRowIndex, setEditableRowIndex] = useState(null);
  const [initialRentValues, setInitialRentValues] = useState(null);
  const DATE_FORMAT = extractDateFormat(intl);

  const handleAddRentClick = () => {
    fields.push({});
    setEditableRowIndex(fields.length);
  };

  return (
    <>
      <div
        sx={{
          maxHeight: 'none',
          overflow: 'visible',
          width: '100%',
        }}
      >
        {fields.map((field, index) => {
          const isRowDisabled = editableRowIndex !== index;
          const isFirstRow = index === 0;
          const pencilBtnMarginTop = isFirstRow ? 27 : undefined;
          const isEditButtonShown = index === fields.length - 1;
          const isTrashButtonShown = !isFirstRow && index === fields.length - 1;

          const handlePencilButtonClick = () => {
            const field = fields.get(index);
            setEditableRowIndex(index);
            setInitialRentValues({ ...field });
          };

          const handleTrashButtonClick = () => {
            const rentId = fields.get(index).id;
            onRowDelete(rentId).then(() => {
              fields.pop();
            });
          };

          const handleUpdate = (values, fallbackValues) => {
            onRowUpdate(values.id, {
              startDate: values.startDate,
              monthlyAmount: values.monthlyAmount,
              notes: values.notes,
            }).catch(() => change(field, { ...fallbackValues }));
          };

          const handleCreate = (values) => {
            onCreate({
              startDate: values.startDate,
              monthlyAmount: values.monthlyAmount,
              notes: values.notes,
            }).catch(() => fields.pop());
          };

          const handleEditSave = () => {
            const field = fields.get(index);

            if (field.id) handleUpdate(field, { ...initialRentValues });
            else handleCreate(field);

            setEditableRowIndex(null);
            setInitialRentValues(null);
          };

          const handleEditCancel = () => {
            setEditableRowIndex(null);

            if (!initialRentValues) {
              fields.pop();
              return;
            }

            change(field, { ...initialRentValues });
            setInitialRentValues(null);
          };

          const dateField = isFirstRow ? (
            <span style={{ marginBottom: 16 }}>
              <ReadOnlyField
                label={intl.formatMessage(messages.startDateLabel)}
                value={
                  <FormattedDateOrDashes
                    value={startDate}
                    format={DATE_FORMAT}
                  />
                }
              />
            </span>
          ) : (
            <div sx={{ position: 'relative', width: '100%' }}>
              <div
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <Field
                  name={`${field}.startDate`}
                  component={renderDateField}
                  bsSize="lg"
                  placeholder={DATE_FORMAT}
                  dateFormat={DATE_FORMAT}
                  validate={[
                    validDate,
                    required,
                    validations.afterPreviousStartDate,
                    validations.beforeLeaseEndDate,
                  ]}
                  disabled={isRowDisabled}
                />
              </div>
              <style>{`
                .rdtPicker {
                  display: none;
                }
                .rdtOpen .rdtPicker {
                  display: block !important;
                  position: absolute !important;
                  top: 100%;
                  left: 0;
                  z-index: 1300;
                  margin-top: 8px;
                  background: white;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.15);
                  border: 1px solid #ccc;
                }
              `}</style>
            </div>
          );
          const actionButtons = isRowDisabled ? (
            <>
              {isEditButtonShown && (
                <Col xs={12} md={1}>
                  <Link
                    style={{
                      display: 'block',
                      fontSize: 18,
                      marginTop: pencilBtnMarginTop,
                    }}
                    onClick={handlePencilButtonClick}
                  >
                    <i className="icon et-pencil" />
                  </Link>
                </Col>
              )}
              {isTrashButtonShown && (
                <Col xs={12} md={1}>
                  <Link
                    style={{
                      display: 'block',
                      fontSize: 18,
                      marginTop: pencilBtnMarginTop,
                    }}
                    onClick={handleTrashButtonClick}
                  >
                    <i className="icon et-trash" />
                  </Link>
                </Col>
              )}
            </>
          ) : (
            <>
              <Col xs={12} md={1}>
                <Link
                  style={{
                    display: 'block',
                    fontSize: 18,
                    marginTop: pencilBtnMarginTop,
                  }}
                  disabled={!isFormValid}
                  onClick={handleEditSave}
                >
                  <i className="icon et-isolated-check" />
                </Link>
              </Col>
              <Col xs={12} md={1}>
                <Link
                  style={{
                    display: 'block',
                    fontSize: 18,
                    marginTop: pencilBtnMarginTop,
                  }}
                  onClick={handleEditCancel}
                >
                  <i className="icon et-delete-3" />
                </Link>
              </Col>
            </>
          );
          const monthlyAmountLabel = isFirstRow
            ? intl.formatMessage(messages.monthlyAmountLabel)
            : undefined;
          const notesLabel = isFirstRow
            ? intl.formatMessage(messages.notesLabel)
            : undefined;

          return (
            <Row key={index}>
              <Col xs={12} md={3}>
                {dateField}
              </Col>
              <Col xs={12} md={3}>
                <Field
                  name={`${field}.monthlyAmount`}
                  label={monthlyAmountLabel}
                  placeholder="0.00"
                  step="0.01"
                  component={renderCurrencyField}
                  required
                  className="input-md form-control"
                  style={{ height: constants.INPUT_HEIGHT }}
                  validate={[required, greaterOrEqual, number]}
                  disabled={isRowDisabled}
                />
              </Col>
              <Col xs={12} md={4}>
                <Field
                  name={`${field}.notes`}
                  label={notesLabel}
                  placeholder={intl.formatMessage(messages.notesPlaceholder)}
                  component={renderTextField}
                  showLabel={isFirstRow}
                  className="input-md form-control"
                  maxLength="200"
                  style={{ height: constants.INPUT_HEIGHT }}
                  validate={[nonEmptyString]}
                  disabled={isRowDisabled}
                />
              </Col>
              {actionButtons}
            </Row>
          );
        })}
      </div>
      <Row>
        <Col xs={12}>
          <Link
            disabled={!R.isNil(editableRowIndex)}
            onClick={handleAddRentClick}
          >
            + {intl.formatMessage(messages.addRent)}
          </Link>
        </Col>
      </Row>
      <div style={{ marginBottom: fields.length < 3 ? 80 : 30 }} />
    </>
  );
}

export default RentScheduleFieldArray;
